import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UiTable from "../../../components/table/Table.component";
import UiModalAlert from "../../../components/modal-alert/ModalAlert.component";
import { PageProps } from "../../../models/routing.model";
import {
	TableColumn,
	TableColumnTemplate,
} from "../../../models/components/table/table-column.model";
import { TableMessages } from "../../../models/components/table/table-message.model";
import { ITablePagination } from "../../../models/components/table/table-pagination.model";
import { TableTemplateButtonsEvent } from "../../../models/components/table/table-template.model";
import { FieldMessageSeverity } from "../../../models/components/field-message.model";
import { Task } from "../../../models/entities/task.model";
import { TaskRow } from "../../../models/pages/task-row.model";
import { getSessionStorageUser } from "../../../services/session/auth.service";
import { renderAlertDetailsCell, renderAlertReasonCell } from "../../../utils/alert-details";
import { useUpdateTask } from "../../../hooks/useUpdateTask";
import { useHideResolvedAlert } from "../../../hooks/useHideResolvedAlert";
import {
	PARTICIPANT_ALERTS_DEFAULT_PAGINATION,
	useParticipantAlerts,
} from "../../../hooks/useParticipantAlerts";

/**
 * "Requires attention" tab in the participant profile (KAN-2355).
 *
 * Same rows and same design as the population-level Requires attention table,
 * scoped to one participant and with fewer columns (no participant identity
 * columns — you are already inside that participant's profile) and no
 * filters/search/sub-tabs. Edit and resolve reuse the population-level modal
 * and mutation, so a change made here shows up in the global table too.
 */
const ParticipantRequiresAttention = (props: PageProps) => {
	const { t } = useTranslation("common");
	const urlParams = useParams<{ participantId: string }>();
	const participantId = Number(urlParams.participantId);

	const [pagination, setPagination] = useState<ITablePagination>({
		...PARTICIPANT_ALERTS_DEFAULT_PAGINATION,
	});

	const { alerts, count, isLoading, error } = useParticipantAlerts(participantId, pagination);
	const updateMutation = useUpdateTask();
	const hideResolvedAlert = useHideResolvedAlert();

	const [alertModal, setAlertModal] = useState<{ entity: Task | null; isVisible: boolean }>({
		entity: null,
		isVisible: false,
	});

	const columns: TableColumn[] = [
		{
			field: "assignee",
			title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.assignedTo",
			template: TableColumnTemplate.COACH,
			sortable: true,
		},
		{
			field: "taskDate",
			title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.date",
			template: TableColumnTemplate.DATE,
			sortable: true,
		},
		{
			field: "reason",
			title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.reason",
			sortable: true,
			template: TableColumnTemplate.CUSTOM,
			templateOptions: {
				customTemplate: (rowData: TaskRow) => renderAlertReasonCell(t, rowData),
			},
		},
		{
			field: "details",
			title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.details",
			sortable: true,
			template: TableColumnTemplate.CUSTOM,
			templateOptions: {
				customTemplate: (rowData: TaskRow) => renderAlertDetailsCell(t, rowData),
			},
		},
		{
			field: "priorityTag",
			title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.severity",
			sortable: true,
			template: TableColumnTemplate.TAG,
		},
		{
			field: "reachouts",
			title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.reachouts",
			sortable: true,
		},
		{
			field: "lastAttempt",
			title: "PARTICIPANTS.ALERTS.TABLE.COLUMNS.lastAttempt",
			template: TableColumnTemplate.DATE,
			sortable: true,
		},
		{
			field: "actionsTemplate",
			template: TableColumnTemplate.BUTTONS,
			sortable: false,
			alignment: "right",
		},
	];

	const editModal = (e: TableTemplateButtonsEvent) =>
		setAlertModal({ entity: (e.row as TaskRow).entity as Task, isVisible: true });

	const cancelAlertModal = () => setAlertModal((prev) => ({ ...prev, isVisible: false }));

	// The modal's own save already invalidates the shared task query keys, so
	// closing it is all this needs to do.
	const completeAlertModal = () => setAlertModal((prev) => ({ ...prev, isVisible: false }));

	/**
	 * Optimistic resolve (KAN-2553 follow-up, same as Home's Critical alerts
	 * and the Requires attention tab): the row and the table total drop BEFORE
	 * the PATCH goes out, and come back if it fails. `useUpdateTask` refetches on success. If
	 * that was the last row of the last page, the paginator sees the lower total
	 * and steps back a page on its own (PrimeReact Paginator).
	 */
	const resolve = async (e: TableTemplateButtonsEvent) => {
		const task = (e.row as TaskRow).entity as Task;
		const restore = await hideResolvedAlert(task.id);
		updateMutation.mutate(
			{
				id: task.id,
				body: {
					resolved: true,
					participantId: task.participantId,
					resolutionSource: "MANUAL",
					resolvedByUserId: getSessionStorageUser().id,
				},
			},
			{ onError: () => restore() }
		);
	};

	// Same empty/loading/error format as the Reminders tab.
	const message = useMemo(() => {
		const messages = new TableMessages();

		if (isLoading) {
			return { severity: FieldMessageSeverity.LOADING, label: messages.loading };
		}

		if (error) {
			return { severity: FieldMessageSeverity.DANGER, label: messages.error };
		}

		if (alerts.length === 0) {
			return { severity: FieldMessageSeverity.INFO, label: messages.empty };
		}

		return undefined;
	}, [isLoading, error, alerts.length]);

	return (
		<>
			<UiTable
				useAsCard
				dataKey="id"
				title={props.title}
				columns={columns}
				value={alerts.map((row) => new TaskRow(row, editModal, resolve))}
				message={message}
				isServerPaginated
				pagination={pagination}
				paginationFn={(event) => setPagination((prev) => ({ ...prev, ...event }))}
				totalRecords={count}
			/>
			{alertModal.isVisible && alertModal.entity && (
				<UiModalAlert
					entity={alertModal.entity}
					isVisible={alertModal.isVisible}
					onCancel={cancelAlertModal}
					onComplete={completeAlertModal}
				/>
			)}
		</>
	);
};

export default ParticipantRequiresAttention;
